import { OperationContext } from "../../sync-engine/OperationContext";
import { Operation } from "../../sync-engine/operations/Operation";
import type { GetSnippetParams, GetSnippetResult, SnippetOoxml } from "@shared/lib/rpc/apis/word-add-in-api";
import { generateRangeId, storeTrackedRange, getTrackedRange } from "../TrackedRangeStore";

/**
 * Word SyncEngine Operation for getting OOXML snippets from the document.
 *
 * Supports multiple source types:
 * - "selection": Extract current cursor selection (expanded to paragraph)
 * - "search": Find paragraphs matching a search pattern
 *
 * Each snippet's range is tracked via Word's tracked objects API and stored
 * in the TrackedRangeStore so we can find and replace it later with setSnippet.
 */
export class GetSnippetOperation implements Operation<GetSnippetResult> {
  constructor(private params: GetSnippetParams) {}

  async commit(context: OperationContext): Promise<GetSnippetResult> {
    if (this.params.source === "selection") {
      return this.getSelectionSnippet(context);
    } else if (this.params.source === "trackedRange") {
      return this.getTrackedRangeSnippet(context);
    } else {
      return this.getMatchingSnippets(context);
    }
  }

  /**
   * Extract snippet from current cursor selection.
   * If the selection is less than a paragraph, expands to the parent paragraph.
   */
  private async getSelectionSnippet(context: OperationContext): Promise<GetSnippetResult> {
    const wordContext = context.wordContext;

    // Get current selection
    const selection = wordContext.document.getSelection();
    selection.load("text");
    await context.sync();

    const selectionText = selection.text || "";
    let targetRange: Word.Range;
    let expandedToParagraph = false;

    if (!selectionText.trim()) {
      // No text selected (cursor only) - expand to containing paragraph
      // Must load paragraphs before accessing them
      selection.load("paragraphs");
      await context.sync();
      const expandedRange = selection.expandTo(selection.paragraphs.getFirst().getRange("Whole"));
      targetRange = expandedRange;
      expandedToParagraph = true;
    } else {
      // Has selected text - check if we need to expand
      const paragraphs = selection.paragraphs;
      paragraphs.load("items");
      await context.sync();

      if (paragraphs.items.length === 1) {
        // Single paragraph - check if partial selection
        const para = paragraphs.items[0];
        para.load("text");
        await context.sync();

        if (selectionText !== para.text) {
          // Partial paragraph - expand
          targetRange = para.getRange("Whole");
          expandedToParagraph = true;
        } else {
          targetRange = selection;
        }
      } else {
        // Multiple paragraphs - use as-is
        targetRange = selection;
      }
    }

    // Get OOXML before tracking
    const ooxml = targetRange.getOoxml();
    targetRange.load("text");
    await context.sync();

    // Track the range so it survives across Word.run batches
    const rangeId = generateRangeId();
    targetRange.track();
    storeTrackedRange(rangeId, targetRange);

    const snippet: SnippetOoxml = {
      // eslint-disable-next-line office-addins/load-object-before-read
      ooxml: ooxml.value,
      expandedToParagraph,
      text: targetRange.text,
      trackedRangeId: rangeId,
    };

    return { snippets: [snippet] };
  }

  /**
   * Extract snippet from a tracked range (from word_add_in_query_range).
   * The tracked range is reused - we just extract OOXML from it.
   */
  private async getTrackedRangeSnippet(_context: OperationContext): Promise<GetSnippetResult> {
    // TypeScript narrowing
    if (this.params.source !== "trackedRange") {
      throw new Error("Expected trackedRange source");
    }

    const { trackedRangeId } = this.params;

    // Get the tracked range from the store
    const trackedRange = getTrackedRange(trackedRangeId);
    if (!trackedRange) {
      throw new Error(
        `Cannot find tracked range "${trackedRangeId}". ` +
          "It may have expired or the document was reloaded. " +
          "Use word_add_in_query_range to find the range again.",
      );
    }

    // Use Word.run with the tracked range to ensure it's valid in a new context.
    // This is critical - tracked ranges from previous Word.run contexts must be
    // accessed through Word.run(trackedObject, ...) to properly rehydrate them.
    let ooxmlValue: string;
    let textValue: string;

    await Word.run(trackedRange, async (wordContext) => {
      const ooxml = trackedRange.getOoxml();
      trackedRange.load("text");
      await wordContext.sync();

      // eslint-disable-next-line office-addins/call-sync-before-read
      ooxmlValue = ooxml.value;
      textValue = trackedRange.text;
    });

    const snippet: SnippetOoxml = {
      ooxml: ooxmlValue!,
      expandedToParagraph: false,
      text: textValue!,
      trackedRangeId: trackedRangeId, // Reuse the same ID
    };

    return { snippets: [snippet] };
  }

  /**
   * Find paragraphs matching a search pattern and extract as snippets.
   * Multiple matches in the same paragraph are deduplicated into one snippet.
   */
  private async getMatchingSnippets(context: OperationContext): Promise<GetSnippetResult> {
    // TypeScript narrowing - we know this is GetSearchSnippetParams
    if (this.params.source !== "search") {
      throw new Error("Expected search source");
    }

    const { search } = this.params;
    const wordContext = context.wordContext;
    const body = wordContext.document.body;

    // Search with options
    const searchResults = body.search(search.searchText, {
      matchCase: search.matchCase ?? false,
      matchWholeWord: search.matchWholeWord ?? false,
      matchWildcards: search.matchWildcards ?? false,
    });

    searchResults.load("items");
    await context.sync();

    if (searchResults.items.length === 0) {
      return { snippets: [] };
    }

    // Load match texts and their containing paragraphs
    for (const matchRange of searchResults.items) {
      matchRange.load("text");
      const paragraphs = matchRange.paragraphs;
      paragraphs.load("items");
    }
    await context.sync();

    // Load paragraph texts for deduplication
    for (const matchRange of searchResults.items) {
      const paragraph = matchRange.paragraphs.items[0];
      if (paragraph) {
        paragraph.load("text");
      }
    }
    await context.sync();

    // Group matches by containing paragraph (deduplicate)
    // Map: paragraph text -> { paragraph, matchTexts[] }
    const paragraphMap = new Map<
      string,
      {
        paragraph: Word.Paragraph;
        matchTexts: string[];
      }
    >();

    for (const matchRange of searchResults.items) {
      const paragraph = matchRange.paragraphs.items[0];
      if (!paragraph) continue;

      const paragraphText = paragraph.text;

      if (paragraphMap.has(paragraphText)) {
        // Add match text to existing paragraph entry
        paragraphMap.get(paragraphText)!.matchTexts.push(matchRange.text);
      } else {
        // New paragraph
        paragraphMap.set(paragraphText, {
          paragraph,
          matchTexts: [matchRange.text],
        });
      }
    }

    // Create snippets for unique paragraphs
    const snippets: SnippetOoxml[] = [];

    for (const { paragraph, matchTexts } of paragraphMap.values()) {
      const paragraphRange = paragraph.getRange("Whole");

      // Get OOXML before tracking
      const ooxmlResult = paragraphRange.getOoxml();
      paragraphRange.load("text");
      await context.sync();

      // Track the range so it survives across Word.run batches
      const rangeId = generateRangeId();
      paragraphRange.track();
      storeTrackedRange(rangeId, paragraphRange);

      snippets.push({
        // eslint-disable-next-line office-addins/load-object-before-read
        ooxml: ooxmlResult.value,
        expandedToParagraph: true,
        text: paragraphRange.text,
        trackedRangeId: rangeId,
        matchTexts,
      });
    }

    return { snippets };
  }

  /**
   * GetSnippetOperation does not merge with other operations.
   * Each request should get the fresh current state.
   */
  merge(_other: Operation<unknown>): Operation<GetSnippetResult> | undefined {
    return undefined;
  }
}
